import { EnvVars } from '../../../environment';
import type { SearchableComboboxOption } from '@/new/components/searchable-combobox';

// `Intl.supportedValuesOf` is a 2022 spec; modern Chromium/Firefox/Safari ship
// it. We type-cast because TS 4.9 doesn't expose the signature on `Intl`.
const intlGlobal = Intl as typeof Intl & {
  supportedValuesOf?: (key: 'timeZone') => string[];
};

// Small curated fallback if the runtime can't enumerate IANA zones.
const FALLBACK_TIME_ZONES = [
  'UTC',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Asia/Tokyo',
  'Asia/Singapore',
  'Asia/Hong_Kong',
  'Asia/Dubai',
  'Australia/Sydney',
];

/**
 * Per-deployment timezone default. `primary` is the fallback if the user's
 * browser zone doesn't belong to the region; `matchers` define what "belongs
 * to the region" means (a browser zone matching any of them is kept verbatim
 * so e.g. a California user on the US deployment doesn't get pinned to NY).
 */
const ENV_TIMEZONE_DEFAULTS: Record<string, { primary: string; matchers: RegExp[] }> = {
  'production-uk': {
    primary: 'Europe/London',
    matchers: [/^Europe\/(London|Belfast|Isle_of_Man|Jersey|Guernsey)$/],
  },
  'production-us': {
    primary: 'America/New_York',
    matchers: [/^America\//, /^US\//, /^Pacific\/Honolulu$/],
  },
  'production-au': {
    primary: 'Australia/Sydney',
    matchers: [/^Australia\//],
  },
  'production-eu': {
    primary: 'Europe/Paris',
    matchers: [/^Europe\//, /^Atlantic\//],
  },
};

export function listTimeZones(): string[] {
  const supported = intlGlobal.supportedValuesOf?.('timeZone');
  if (supported && supported.length > 0) return supported;
  return FALLBACK_TIME_ZONES;
}

function browserTimeZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

/**
 * Resolve the default timezone for the current deployment:
 *  - For a region-specific production env (UK / US / AU / EU): keep the
 *    browser zone if it falls inside that region, otherwise fall back to the
 *    region's primary zone.
 *  - For `local` / `staging` (or any env we don't recognise): use the browser
 *    zone, falling back to UTC.
 */
export function detectTimeZone(): string {
  const browser = browserTimeZone();
  const envDefault = ENV_TIMEZONE_DEFAULTS[EnvVars.ENVIRONMENT];

  if (envDefault) {
    if (browser && envDefault.matchers.some((m) => m.test(browser))) return browser;
    return envDefault.primary;
  }

  return browser ?? 'UTC';
}

/**
 * Current UTC offset for an IANA zone in the form "+01:00", "-04:00", or
 * "+00:00". Returns null if the runtime can't resolve the zone. Computed at
 * "now", so DST is reflected: the same zone may render with a different
 * offset across the year.
 */
export function getTimeZoneOffset(zone: string): string | null {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      timeZoneName: 'longOffset',
    }).formatToParts(new Date());
    const raw = parts.find((p) => p.type === 'timeZoneName')?.value;
    if (raw === undefined) return null;
    if (raw === 'GMT') return '+00:00';
    return raw.replace(/^GMT/, '');
  } catch {
    return null;
  }
}

/** Display label e.g. `"Europe/London (UTC+01:00)"`. */
export function formatTimeZoneLabel(zone: string): string {
  const offset = getTimeZoneOffset(zone);
  return offset !== null ? `${zone} (UTC${offset})` : zone;
}

export function getTimeZoneOptions(): SearchableComboboxOption[] {
  return listTimeZones().map((tz) => ({ label: formatTimeZoneLabel(tz), value: tz }));
}
